import {
  deriveFeatureFlagMapFromArray,
  FeatureFlagKey,
  type FeatureFlagMap,
} from "encamp-shared/src/featureFlags";
import { createContext, useContext, useMemo } from "react";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

/**
 * A flag resolved for a tenant by the server (`Tenant.featureFlags`): `value`
 * is the stored setting (TenantConfig → DB global default → false) with NO
 * staff override applied. `staffOverride`/`inDevelopment` ride along so the
 * client can apply the staff view and preview affordances.
 */
export type ResolvedFlag = {
  key: string;
  value: boolean;
  staffOverride: boolean;
  inDevelopment: boolean;
};

/**
 * Build the resolved flag map from the server-resolved `Tenant.featureFlags`.
 * The server already folds in the DB global default, so the client only OR-s
 * in the staff override for staff users. Shared by useFeatureFlags and the
 * tenant-scoped provider so both compute flags identically.
 */
export function buildFeatureFlagMap(
  resolvedFlags: readonly ResolvedFlag[] | undefined,
  isStaff: boolean
): FeatureFlagMap {
  // OR the staff override into the stored value per flag, then let
  // deriveFeatureFlagMapFromArray fill every known key (unknown keys are
  // dropped, absent keys default to false). No defaults map: the server
  // already folded the DB global default into `value`.
  const rows = (resolvedFlags ?? []).map((flag) => ({
    key: flag.key,
    value: flag.value || (isStaff && flag.staffOverride),
  }));
  return deriveFeatureFlagMapFromArray(rows);
}

/**
 * True when a staff user is viewing an in-development flag that is off for the
 * tenant (ignoring the staff override). Drives the "preview" affordance in nav
 * and page chrome. Reads `inDevelopment` + the stored `value` off the same
 * server-resolved `Tenant.featureFlags` list — no client-side metadata.
 */
export function isStaffPreviewingFlag(
  flag: FeatureFlagKey,
  resolvedFlags: readonly ResolvedFlag[] | undefined,
  isStaff: boolean
): boolean {
  if (!isStaff) return false;
  const entry = (resolvedFlags ?? []).find((f) => f.key === flag);
  // Absent entry (still loading / newly-added key) → treat as in-development
  // and off, matching the old code-constant stub behavior.
  const inDevelopment = entry?.inDevelopment ?? true;
  const storedOn = entry?.value ?? false;
  return inDevelopment && !storedOn;
}

/**
 * Override the ambient (current-tenant) flags for a subtree. Used by the staff Manifest
 * Review queue, which mounts customer manifest UIs for OTHER tenants and must render
 * their flags — not the staff user's current/session tenant. Null = no override.
 *
 * This map is built with the staff override applied (so staff see the reviewed
 * tenant's flags the way staff see everything). For surfaces that must mirror a
 * backend gate on the *stored* value, use {@link StoredFeatureFlagOverrideContext}
 * via {@link useStoredFeatureFlags} instead.
 */
export const FeatureFlagOverrideContext = createContext<FeatureFlagMap | null>(null);

/**
 * Companion to {@link FeatureFlagOverrideContext} carrying the reviewed tenant's
 * flags WITHOUT the staff override — i.e. the stored `TenantConfig` values. Lets
 * {@link useStoredFeatureFlags} reflect the real stored row even inside the staff
 * Manifest Review subtree (where a staff-override flag like `waste-module` would
 * otherwise read `true` regardless of the reviewed tenant's actual config).
 */
export const StoredFeatureFlagOverrideContext = createContext<FeatureFlagMap | null>(null);

export function useFeatureFlags(): {
  featureFlags: FeatureFlagMap;
  loading: boolean;
} {
  const override = useContext(FeatureFlagOverrideContext);
  const { tenant, loading: tenantLoading } = useTenant();
  const { isStaff, loading: userLoading } = useCurrentUser();

  const featureFlags = useMemo(
    () => buildFeatureFlagMap(tenant?.featureFlags, isStaff),
    [tenant?.featureFlags, isStaff]
  );

  if (override) return { featureFlags: override, loading: false };
  return { featureFlags, loading: tenantLoading || userLoading };
}

/**
 * Resolved flags WITHOUT the staff override — reflects the tenant's *stored* config.
 *
 * Use when a UI affordance must match a backend gate that reads the stored
 * `TenantConfig` value rather than the staff-override view. For example, manifest
 * ingestion is gated on the real `waste-module` row (`canProcessManifests` /
 * `buildDocumentWhereClause`), so a staff-overridden `waste-module` would show an
 * upload/reprocess action that the backend then no-ops. In a tenant-flag override
 * subtree (staff Manifest Review) we read the dedicated stored override
 * ({@link StoredFeatureFlagOverrideContext}) — NOT the staff-overridden override
 * map — so the reviewed tenant's actual stored row is reflected even for
 * staff-override flags like `waste-module`.
 */
export function useStoredFeatureFlags(): {
  featureFlags: FeatureFlagMap;
  loading: boolean;
} {
  const storedOverride = useContext(StoredFeatureFlagOverrideContext);
  const { tenant, loading: tenantLoading } = useTenant();

  const featureFlags = useMemo(
    () => buildFeatureFlagMap(tenant?.featureFlags, false),
    [tenant?.featureFlags]
  );

  if (storedOverride) return { featureFlags: storedOverride, loading: false };
  return { featureFlags, loading: tenantLoading };
}
